import React, { useState } from "react";
import { Stack, useTheme, Tooltip } from "@mui/material";
import { Legend, PolarAngleAxis, RadialBar, RadialBarChart, ResponsiveContainer, LegendType } from "recharts";
import { Tooltip as RechartsTooltip } from 'recharts';
import RadialSentimentTooltip from './RadialSentimentTooltip';
import { defaultSettings } from "./RadialSentimentSettings";
import WidgetLayout from 'components/widgetParts/WidgetLayout';
import WidgetTitle from 'components/widgetParts/WidgetTitle';
import { useTranslation } from "react-i18next";
import { Query } from "system/query/Query";
import { DashboardQueryDrilldownDialog } from "components/dialogs/QueryDrilldown/DashboardQueryDrilldownDialog";
import { QueryInfo } from "system/query/QueryInfo";
import { WidgetRequest } from "system/widget/Widget";

interface RadialSentimentChartProps {
  /**
   * State object represented by series of key value pairs,
   * where the key is a string which represents the title of the
   * updatable settings option.
   */
  settingsState: RadialSentimentChartProps | {};
  data?: BarData[];
  isPreview?: boolean;

  queryInfo?: QueryInfo;
  queryDisplayTitle?: string;
  originalRequest?: WidgetRequest;
  activeQueryIds?: string[];
}

interface BarData {
  name: string;
  key: string;
  value: number;
  percent: string;
  fill: string;
}

export default function RadialSentimentChart(props: RadialSentimentChartProps) {
  const theme = useTheme();
  const { i18n } = useTranslation();
  const namedColors = theme.colors.namedColors;
  const currentState = {...defaultSettings, ...props.settingsState};
  const widgetData: BarData[] = props.data || [];
  const dummyData: BarData[] = [
    { name: i18n.t("neutral"), key: "0", value: 150, percent: "15", fill: namedColors["neutral"] },
    { name: i18n.t("negative"), key: "n", value: 250, percent: "25", fill: namedColors["negative"] },
    { name: i18n.t("positive"), key: "p", value: 600, percent: "60", fill: namedColors["positive"] },
  ];

  const sortedData = (props.isPreview ? dummyData : widgetData).sort((a: any, b: any) => {
    const order = ["0", "n", "p"];
    return order.indexOf(a.key) - order.indexOf(b.key);
  });

  const [hoveredBar, setHoveredBar] = useState<any>(null);
  const [selectedBar, setSelectedBar] = useState<any>(null);

  return (
    <Tooltip
      title={(hoveredBar) ? <RadialSentimentTooltip bar={hoveredBar} /> : ''}
      open={Boolean(hoveredBar)}
      followCursor={true}
    >
      <Stack
        sx={{
          width: props.isPreview ? "400px" : "100%",
          height: props.isPreview ? "300px" : "100%"
        }}>
        <WidgetLayout
          header={
            <WidgetTitle
              alignment={currentState.widgetTitleAlignment}
              settingsTitle={currentState.widgetTitle}
              defaultTitle={i18n.t("widget-types.radial-sentiment.widget.label")}
              customTitle={currentState.customWidgetTitle}
              widgetQueryTitle={props.queryDisplayTitle}
            />
          }
        >
          <ResponsiveContainer>
            <RadialBarChart
              cy={"50%"}
              data={sortedData}
              startAngle={180}
              endAngle={0}
              innerRadius="25%"
              outerRadius="100%"
            >
              <PolarAngleAxis domain={[0, 100]} tick={false} type="number" />
              <RechartsTooltip content={() => null} />

              {/* Bars */}
              <RadialBar data={sortedData} dataKey="percent" background
                onMouseEnter={(bar) => setHoveredBar(bar)}
                onMouseLeave={() => setHoveredBar(null)}
                onClick={(bar) => { setSelectedBar(bar) }}
              />

              {/* Legend */}
              {currentState?.showLegend &&
                <Legend
                  formatter={value =>
                    <span style={{ color: theme.palette.text.primary, fontSize: "0.8rem" }}>{value}</span>
                  }
                  payload={sortedData.map((item: any) => {
                    const nameCapitalized = item.name.charAt(0).toUpperCase() + item.name.slice(1);
                    return {
                      value: `${nameCapitalized} (${item.percent}%)`,
                      type: "square" as LegendType,
                      color: item.fill
                    }}).reverse()
                  }
                />
              }
            </RadialBarChart>
          </ResponsiveContainer>
          {selectedBar &&
            <DashboardQueryDrilldownDialog
              title={selectedBar.name}
              queryInfo={props.queryInfo}
              onClose={() => setSelectedBar(null)}
              activeQueryIds={props.activeQueryIds}
              widgetRequestInfo={props.originalRequest}
              drilldownQuery={new Query({ query: selectedBar.query.query })}
            />
          }
        </WidgetLayout>
      </Stack>
    </Tooltip>
  );
}